import CalendarOutlined from '@ant-design/icons/CalendarOutlined'
import CheckOutlined from '@ant-design/icons/CheckOutlined'
import InfoCircleTwoTone from '@ant-design/icons/InfoCircleTwoTone'
import LockOutlined from '@ant-design/icons/LockOutlined'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import JSZip from 'jszip'
import * as FileSaver from 'file-saver'
import {
  Button,
  Dropdown,
  Menu,
  message,
  Modal,
  Steps,
  Tooltip,
  Upload,
} from 'antd'
import { inject, observer } from 'mobx-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { blue } from '../../../../color'
import { AvatarHasName } from '../../../../components/Common/Avatar'
import {
  EmptyText,
  TitleContentBox,
} from '../../../../components/Common/CellText'
import FileItem from '../../../../components/Common/FileItemComponent/FileItem'
import LinkedWork from '../../../../components/Common/Linked/LinkedWork'
import ContentBlockWrapper from '../../../../components/ContentBlockWrapper'
import PDFViewer from '../../../../components/PDFViewer/PDFViewer'
import PopupRenameFile from '../../../../components/PopupRenameFile'
import PopupTPApproverSign from '../../../../components/PopupSign/PopupTPApproverSign'
import SelectPeoplePopup from '../../../../components/SelectPeoplePopup/SelectPeoplePopup'
import SpinnerInlineComponent from '../../../../components/SpinnerInlineComponent/SpinnerInlineComponent'
import {
  ACL_ACTION_TYPE,
  ASSIGNEE_TYPE,
  DATE_FORMAT_DEFAULT,
  ELECTRONIC_DOCUMENT_STATUS,
} from '../../../../constants'
import dayjs from '../../../../dayjsConfig'
import utils from '../../../../utils'
import { CustomStepIcon } from '../../WorkDocumentSignDocPage/PopupCreateSignDoc/PopupCreateSignDocStyled'
import CommentBlock from '../CommentBlock'
import PopupReleaseElectronicDocument from '../PopupReleaseElectronicDocument'
import InternalDocumentSignDocHistory from '../WorkDocumentSignDocHistory/InternalDocumentSignDocHistory'
import MainFilesList from './components/MainFilesList'
import PopupRejectDocument, { MAX_ROLLBACK } from './PopupRejectDocument'
import { useCanvasDocPermissions } from './hooks/useCanvasDocPermissions'
import { useCanvasDocSigning } from './hooks/useCanvasDocSigning'
import { useMainFileViewer } from './hooks/useMainFileViewer'
import {
  areAllPdfFilesSigned,
  countUnsignedPdfFiles,
  getSignRequirementForStep,
  resolveActiveSignConfig,
} from './utils/canvasDocSignStepConfig'
import { isFileSignedAtStep } from './utils/canvasDocFileHistory'
import {
  BlockRight,
  ButtonItem,
  CanvasDocHeader,
  CanvasDocWrapper,
  ElectronicDocumentInfo,
  FlowLockedAction,
  FlowLockedBanner,
  FlowLockedContent,
  FlowLockedDesc,
  FlowLockedIcon,
  FlowLockedText,
  FlowLockedTitle,
  RollbackCountBadge,
  RollbackCountValue,
  ViewerWrapper,
} from './CanvasDocStyled'

import AccessControlAction from '../../../../components/AccessControlAction'

const { confirm } = Modal
const { Step } = Steps

const CanvasDoc = props => {
  const {
    signedDocumentStore,
    loadingAnimationStore,
    fileStore,
    electronicDocumentStore,
    listHistoryLogStore,
    authenticationStore,
    selectPeopleStore,
    aclStore,
    getElectronicDocumentById,
  } = props
  const { selectedElectronicDocument } = electronicDocumentStore
  const { historyLists } = listHistoryLogStore
  const { checkAccessControlAction } = aclStore
  const { currentUser } = authenticationStore
  const {
    setSelectUserData,
    selectUserData,
    setSelectDepartmentData,
    selectDepartmentData,
    originDepartmentWithUserList,
    handleCancelSelectUserDepartment,
    setIsVisibleSelectUserDepartmentPopup,
    isVisibleSelectUserDepartmentPopup,
  } = selectPeopleStore

  const {
    action_history,
    assignees,
    attachments,
    annex_attachments,
    main_files,
    code,
    status,
    created_date,
    linked_work,
    permission,
  } = selectedElectronicDocument || {}
  const linkedWorkList = linked_work && linked_work.code ? [linked_work] : []
  const { documentAttachment } = fileStore

  // Centralized popup visibility state
  const [popupVisibility, setPopupVisibility] = useState({
    regisNumber: false,
    release: false,
    vtSign: false,
    tpSign: false,
    tpCoSign: false,
    reject: false,
  })
  const [rejectLoading, setRejectLoading] = useState(false)

  const [currentStep, setCurrentStep] = useState(0)
  const [statusStep, setStatusStep] = useState(Array(9).fill('await'))
  const [fileActive, setFileActive] = useState(null)
  const [fileActiveIsPDF, setFileActiveIsPDF] = useState(false)
  const [handlerName, setHandlerName] = useState({
    creator_name: undefined,
    ktat_office_name: undefined,
    leader_approval_name: undefined,
    lead_ktat_office_name: undefined,
    form_approval_name: undefined,
    bod_approval_name: undefined,
    regis_number_name: undefined,
    sign_document_name: undefined,
    release_name: undefined,
  })

  const [skipSignStates, setSkipSignStates] = useState({
    sign: false,
    tp: false,
    lead: false,
    num: false,
  })
  const [renameFileState, setRenameFileState] = useState({
    visible: false,
    ext: '',
    fields: [{ name: ['rename'], value: '' }],
  })
  const [currentCoSignerIndex, setCurrentCoSignerIndex] = useState(0)
  const [signedFilesTracking, setSignedFilesTracking] = useState({
    LEADER_SIGN: new Set(),
    LEAD_KTAT_SIGN: new Set(),
    BOD_SIGN: new Set(),
    DIGITAL_SIGN: new Set(),
  })

  const {
    checkPermission,
    isAssigneeOfStep,
    assigneesMap,
    coSigners,
    userList,
  } = useCanvasDocPermissions({ permission, assignees, currentUser })

  const {
    selectedFileToView,
    viewingFileAttachment,
    handleSelectFileToView,
    clearFileView,
    resetFileView,
    showFileAt,
    refreshFileViewAtIndex,
  } = useMainFileViewer({
    main_files,
    fileStore,
    loadingAnimationStore,
  })

  const {
    afterSignHandlers,
    handleDirectSignForFile,
    handleBatchSignAllForStep,
  } = useCanvasDocSigning({
    code,
    main_files,
    action_history,
    signedDocumentStore,
    electronicDocumentStore,
    fileStore,
    loadingAnimationStore,
    clearFileView,
    refreshFileViewAtIndex,
  })

  const activeSignConfig = useMemo(
    () =>
      resolveActiveSignConfig({
        currentStep,
        isAssigneeOfStep,
        afterSignHandlers,
      }),
    [afterSignHandlers, currentStep, isAssigneeOfStep]
  )

  const currentApprovalBlocker = useMemo(() => {
    const signReq = getSignRequirementForStep(currentStep)
    if (!signReq) {
      return { canApprove: true, unsignedCount: 0 }
    }
    const unsignedCount = countUnsignedPdfFiles(main_files, signReq.isSigned)
    return {
      canApprove: unsignedCount === 0,
      unsignedCount,
    }
  }, [currentStep, main_files])

  const validateAllFilesSignedBeforeApproval = useCallback(
    approvalStep => {
      const signReq = getSignRequirementForStep(approvalStep)
      if (!signReq || areAllPdfFilesSigned(main_files, signReq.isSigned)) {
        return true
      }
      const unsignedCount = countUnsignedPdfFiles(main_files, signReq.isSigned)
      message.warning(
        `Vui lòng ký số tất cả file PDF trước khi duyệt! (Còn ${unsignedCount} file chưa ký)`
      )
      return false
    },
    [main_files]
  )

  useEffect(() => {
    const loadSignedFilesInfo = async () => {
      if (!code || !status) return

      try {
        const signSteps = [
          'LEADER_SIGN',
          'LEAD_KTAT_SIGN',
          'BOD_SIGN',
          'DIGITAL_SIGN',
        ]
        const newTracking = { ...signedFilesTracking }

        for (const step of signSteps) {
          // Parse action_history để tìm files đã ký
          const signedLogs =
            action_history?.filter(h => h.action === `FILE_SIGNED_${step}`) ||
            []

          const signedFileIndices = new Set()
          signedLogs.forEach(log => {
            try {
              const data = JSON.parse(log.dataAfter || '{}')
              if (data.signedFileIndex !== undefined) {
                signedFileIndices.add(data.signedFileIndex)
              }
            } catch (e) {
              console.error('Error parsing signed file info:', e)
            }
          })

          newTracking[step] = signedFileIndices
        }

        setSignedFilesTracking(newTracking)
      } catch (err) {
        console.error('Error loading signed files info:', err)
      }
    }

    loadSignedFilesInfo()
  }, [code, status, action_history])

  // Helper function để check xem file đã được ký chưa
  const isFileSigned = useCallback(
    (fileIndex, signStep) =>
      isFileSignedAtStep(signedFilesTracking, fileIndex, signStep),
    [signedFilesTracking]
  )

  useEffect(() => {
    if (props.onStepChange) {
      props.onStepChange(currentStep)
    }
  }, [currentStep, props.onStepChange])

  const getHistoryList = useCallback(async () => {
    if (code) {
      try {
        await listHistoryLogStore.getLists(code)
      } catch (error) {
        console.log(error)
        message.error(error?.vi || 'Đã có lỗi xảy ra!')
      }
    }
  }, [code, listHistoryLogStore])

  useEffect(() => {
    if (code) {
      getHistoryList()
    }
  }, [code, status, getHistoryList])

  useEffect(() => {
    if (fileActive?.file_id === null) {
      getElectronicDocumentById()
    }
  }, [fileActive?.file_id, getElectronicDocumentById])

  // ==================== CORE ACTION HANDLERS ====================

  const handleAction = useCallback(
    async (action, fileId, successMessage, permissionType, preCheck) => {
      if (permissionType && !checkPermission(permissionType)) {
        message.warning('Bạn không có quyền thực hiện hành động này!')
        return
      }

      if (preCheck && !preCheck()) return
      try {
        loadingAnimationStore.showSpinner(true)
        await electronicDocumentStore.updateElectronicDocumentStatus(
          { action, file_id: fileId },
          code
        )
        await electronicDocumentStore.getElectronicDocumentById(code)
        message.success(successMessage)

        if (fileId) {
          await fileStore.downloadAttachment(fileId)
        }
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Đã có lỗi xảy ra!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    },
    [
      code,
      checkPermission,
      electronicDocumentStore,
      loadingAnimationStore,
      fileStore,
    ]
  )

  const checkStepPermission = useCallback(
    (step, permissionType, assignee, messageText) => {
      if (checkPermission(permissionType) && !assignee) {
        message.warning(messageText)
        return false
      }
      return true
    },
    [checkPermission]
  )

  // Thứ tự bước workflow — dùng để validate người bước kế tiếp khi bấm Duyệt
  const WORKFLOW_STEPS = useMemo(
    () => [
      {
        permissionType: ASSIGNEE_TYPE.LEADER_APPROVER,
        assigneeKey: 'leaderApprover',
        message: 'Bạn chưa chọn Trưởng đơn vị ký!',
      },
      {
        permissionType: ASSIGNEE_TYPE.KTAT_OFFICE,
        assigneeKey: 'ktatOffice',
        message: 'Bạn chưa chọn P.KTAT duyệt thể thức!',
      },
      {
        permissionType: ASSIGNEE_TYPE.LEAD_KTAT_OFFICE,
        assigneeKey: 'leadKtatOffice',
        message: 'Bạn chưa chọn TP.KTAT ký!',
      },
      {
        permissionType: ASSIGNEE_TYPE.BOARD_OF_DIRECTOR,
        assigneeKey: 'boardOfDirector',
        message: 'Bạn chưa chọn Giám đốc ký!',
      },
      {
        permissionType: ASSIGNEE_TYPE.DIGITAL_SIGNER,
        assigneeKey: 'digitalSigner',
        message: 'Bạn chưa chọn người đóng dấu!',
      },
      {
        permissionType: ASSIGNEE_TYPE.RELEASER,
        assigneeKey: 'releaser',
        message: 'Bạn chưa chọn người phát hành!',
      },
    ],
    []
  )

  const validateNextAssigneeOnApprove = useCallback(
    currentPermissionType => {
      const currentIndex = currentPermissionType
        ? WORKFLOW_STEPS.findIndex(
          step => step.permissionType === currentPermissionType
        )
        : -1

      for (let i = currentIndex + 1; i < WORKFLOW_STEPS.length; i++) {
        const step = WORKFLOW_STEPS[i]
        if (!checkPermission(step.permissionType)) continue
        if (!assigneesMap[step.assigneeKey]) {
          message.warning(step.message)
          return false
        }
        // Chỉ check bước kế tiếp có trong quy trình
        return true
      }
      return true
    },
    [WORKFLOW_STEPS, checkPermission, assigneesMap]
  )

  // ==================== STEP 0: TẠO DỰ THẢO ====================

  const handlePresentDuThao = useCallback(() => {
    if (!validateNextAssigneeOnApprove(null)) {
      return
    }

    handleAction(
      ELECTRONIC_DOCUMENT_STATUS.CRE_APPROVAL,
      fileActive?.file_id,
      'Trình dự thảo thành công!',
      null
    )
  }, [fileActive?.file_id, handleAction, validateNextAssigneeOnApprove])

  const onSelectDocument = useCallback(
    async file => {
      const formData = new FormData()
      formData.append('file', file.file)
      loadingAnimationStore.showSpinner(true)
      try {
        const res = await fileStore.uploadFile(formData)
        await electronicDocumentStore.updateElectronicDocumentStatus(
          {
            action: ELECTRONIC_DOCUMENT_STATUS.CHANGE_DOC,
            file_id: res.data.file_id,
          },
          code
        )
        const response = await electronicDocumentStore.getElectronicDocumentById(
          code
        )
        await fileStore.downloadAttachment(
          response.data.action_history.find(
            el => el.action === response.data.status
          )?.file_id
        )
        await getHistoryList()
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Đã có lỗi xảy ra!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    },
    [
      code,
      electronicDocumentStore,
      fileStore,
      getHistoryList,
      loadingAnimationStore,
    ]
  )

  const isFlowLocked = status === ELECTRONIC_DOCUMENT_STATUS.FLOW_LOCKED

  const rollbackCount = useMemo(() => {
    const source =
      historyLists?.length > 0 ? historyLists : action_history || []
    if (!source.length) return 0
    const sorted = [...source].sort((a, b) => (b.id || 0) - (a.id || 0))
    let count = 0
    for (const item of sorted) {
      if (item.action === ELECTRONIC_DOCUMENT_STATUS.FLOW_UNLOCK) break
      if (
        item.action === ELECTRONIC_DOCUMENT_STATUS.ROLLBACK_TO_CREATOR ||
        item.action === ELECTRONIC_DOCUMENT_STATUS.ROLLBACK_TO_LEADER ||
        item.action === ELECTRONIC_DOCUMENT_STATUS.FLOW_LOCKED
      ) {
        count += 1
      }
    }
    return count
  }, [action_history, historyLists])

  const openRejectPopup = useCallback(() => {
    if (isFlowLocked) {
      message.warning('Luồng trình ký đã bị khóa!')
      return
    }
    setPopupVisibility(prev => ({ ...prev, reject: true }))
  }, [isFlowLocked])

  const handleRejectSubmit = useCallback(
    async ({ return_to_step, reason }) => {
      try {
        setRejectLoading(true)
        resetFileView?.()
        await electronicDocumentStore.rollbackElectronicDocument(code, {
          return_to_step,
          reason,
        })
        const {
          data,
        } = await electronicDocumentStore.getElectronicDocumentById(code)
        if (data?.main_files?.length > 0) {
          await showFileAt?.(data.main_files[0])
        }
        await getHistoryList?.()
        setPopupVisibility(prev => ({ ...prev, reject: false }))

        if (data?.status === ELECTRONIC_DOCUMENT_STATUS.FLOW_LOCKED) {
          message.error(
            'Hồ sơ đã bị khóa luồng trình ký do vượt quá 02 lần trả về chỉnh sửa!'
          )
        } else {
          message.success(
            return_to_step === 1
              ? 'Đã trả hồ sơ về Bước 1 (Người lập)!'
              : 'Đã trả hồ sơ về Bước 2 (Trưởng đơn vị)!'
          )
        }
      } catch (err) {
        console.error(err)
        message.error(err?.vi || err?.message || 'Đã có lỗi xảy ra!')
      } finally {
        setRejectLoading(false)
      }
    },
    [code, electronicDocumentStore, getHistoryList, resetFileView, showFileAt]
  )

  const handleUnlockFlow = useCallback(async () => {
    try {
      loadingAnimationStore.showSpinner(true)
      await electronicDocumentStore.unlockElectronicDocument(code, {
        reason: 'Giám đốc quyết định mở lại luồng trình ký',
      })
      await electronicDocumentStore.getElectronicDocumentById(code)
      await getHistoryList?.()
      message.success('Đã mở lại luồng trình ký!')
    } catch (err) {
      console.error(err)
      message.error(err?.vi || err?.message || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [code, electronicDocumentStore, getHistoryList, loadingAnimationStore])

  const renderRejectButton = useCallback(
    () => (
      <div
        style={{
          display: 'flex',
          width: 380,
          marginTop: 8,
          gap: 8,
        }}>
        <Button
          onClick={openRejectPopup}
          danger
          type="primary"
          disabled={isFlowLocked}
          style={{ flex: 1 }}>
          Từ chối
        </Button>
      </div>
    ),
    [isFlowLocked, openRejectPopup]
  )

  // ==================== STEP 2: PHÒNG KTAT DUYỆT ====================

  const handleKTATApproval = useCallback(() => {
    if (!fileActive?.file_id) {
      message.error('Không tìm thấy tài liệu để duyệt!')
      return
    }

    if (!validateNextAssigneeOnApprove(ASSIGNEE_TYPE.KTAT_OFFICE)) {
      return
    }

    handleAction(
      ELECTRONIC_DOCUMENT_STATUS.KTAT_OFFICE_APPROVAL,
      fileActive.file_id,
      'P.KTAT đã duyệt thể thức thành công!',
      ASSIGNEE_TYPE.KTAT_OFFICE
    )
  }, [fileActive, handleAction, validateNextAssigneeOnApprove])

  // ==================== STEP 1: LÃNH ĐẠO DUYỆT ====================

  const handleSkipSignLeaderApproval = useCallback(async () => {
    try {
      loadingAnimationStore.showSpinner(true)

      if (!validateNextAssigneeOnApprove(ASSIGNEE_TYPE.LEADER_APPROVER)) {
        return
      }

      const fileIdToUpdate =
        action_history?.find(
          el => el.action === ELECTRONIC_DOCUMENT_STATUS.CRE_APPROVAL
        )?.file_id || fileActive?.file_id

      if (!fileIdToUpdate) {
        message.error('Không tìm thấy file để duyệt!')
        return
      }

      await electronicDocumentStore.updateElectronicDocumentStatus(
        {
          action: ELECTRONIC_DOCUMENT_STATUS.LEAD_APPROVAL,
          file_id: fileIdToUpdate,
        },
        code
      )
      const { data } = await electronicDocumentStore.getElectronicDocumentById(
        code
      )
      await fileStore.downloadAttachment(
        data.action_history.find(el => el.action === data.status)?.file_id
      )
      message.info('Trưởng đơn vị bỏ qua ký số!')
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    action_history,
    code,
    electronicDocumentStore,
    fileStore,
    fileActive,
    loadingAnimationStore,
    validateNextAssigneeOnApprove,
  ])

  // ==================== STEP 3: TRƯỞNG PHÒNG KTAT DUYỆT ====================

  const handleSkipSignLeadKTATApproval = useCallback(async () => {
    try {
      loadingAnimationStore.showSpinner(true)
      if (!validateNextAssigneeOnApprove(ASSIGNEE_TYPE.LEAD_KTAT_OFFICE)) {
        return
      }

      const fileIdToUpdate =
        action_history?.find(
          el => el.action === ELECTRONIC_DOCUMENT_STATUS.KTAT_OFFICE_APPROVAL
        )?.file_id || fileActive?.file_id

      if (!fileIdToUpdate) {
        message.error('Không tìm thấy file để duyệt!')
        return
      }

      await electronicDocumentStore.updateElectronicDocumentStatus(
        {
          action: ELECTRONIC_DOCUMENT_STATUS.LEAD_KTAT_OFFICE_APPROVAL,
          file_id: fileIdToUpdate,
        },
        code
      )
      const { data } = await electronicDocumentStore.getElectronicDocumentById(
        code
      )
      await fileStore.downloadAttachment(
        data.action_history.find(el => el.action === data.status)?.file_id
      )
      message.info('TP.KTAT bỏ qua ký số')
    } catch (err) {
      console.log(err)
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    action_history,
    code,
    electronicDocumentStore,
    fileStore,
    fileActive,
    loadingAnimationStore,
    validateNextAssigneeOnApprove,
  ])

  // ==================== STEP 4: DUYỆT THỂ THỨC ====================

  const handleFormApproval = useCallback(() => {
    if (
      checkPermission(ASSIGNEE_TYPE.BOARD_OF_DIRECTOR) &&
      !assigneesMap.boardOfDirector
    ) {
      message.warning('Bạn chưa chọn Giám đốc ký!')
      return
    }

    handleAction(
      ELECTRONIC_DOCUMENT_STATUS.FORM_APPROVAL,
      fileActive?.file_id,
      'Duyệt thể thức thành công!',
      ASSIGNEE_TYPE.FORM_APPROVER
    )
  }, [assigneesMap, checkPermission, fileActive, handleAction])

  // ==================== STEP 5: BGĐ DUYỆT ====================

  const handleSkipBGDSign = useCallback(() => {
    if (!validateNextAssigneeOnApprove(ASSIGNEE_TYPE.BOARD_OF_DIRECTOR)) {
      return
    }

    handleAction(
      ELECTRONIC_DOCUMENT_STATUS.BOARD_OF_DIRECTOR_APPROVAL,
      fileActive?.file_id,
      'Đã bỏ qua bước ký số!',
      ASSIGNEE_TYPE.BOARD_OF_DIRECTOR,
      () => {
        setSkipSignStates(prev => ({ ...prev, lead: true }))
        return true
      }
    )
  }, [fileActive, handleAction, validateNextAssigneeOnApprove])

  const handleRevertToLeaderApproval = useCallback(async () => {
    try {
      loadingAnimationStore.showSpinner(true)

      const leaderApprovalFileId =
        action_history?.find(
          el =>
            el.action === ELECTRONIC_DOCUMENT_STATUS.LEAD_KTAT_OFFICE_APPROVAL
        )?.file_id ||
        action_history?.find(
          el => el.action === ELECTRONIC_DOCUMENT_STATUS.FORM_APPROVAL
        )?.file_id

      if (!leaderApprovalFileId) {
        message.error('Không tìm thấy file để duyệt lại!')
        return
      }

      await electronicDocumentStore.updateElectronicDocumentStatus(
        {
          action: ELECTRONIC_DOCUMENT_STATUS.BOARD_OF_DIRECTOR_REJECTION,
          file_id: leaderApprovalFileId,
        },
        code
      )

      const { data } = await electronicDocumentStore.getElectronicDocumentById(
        code
      )

      await fileStore.downloadAttachment(
        data.action_history.find(el => el.action === data.status)?.file_id
      )

      const hasLeadKtat = checkPermission(ASSIGNEE_TYPE.LEAD_KTAT_OFFICE)
      const hasLeader = checkPermission(ASSIGNEE_TYPE.LEADER_APPROVER)
      const hasForm = checkPermission(ASSIGNEE_TYPE.FORM_APPROVER)
      const hasDepartment = checkPermission(ASSIGNEE_TYPE.DEPARTMENT_APPROVER)

      let messageText = 'Đã quay lại bước Tạo dự thảo!'

      if (hasLeadKtat) {
        messageText = 'Đã quay lại bước TP.KTAT ký!'
      } else if (hasForm) {
        messageText = 'Đã quay lại bước Duyệt thể thức!'
      } else if (hasLeader) {
        messageText = 'Đã quay lại bước Trưởng đơn vị ký!'
      } else if (hasDepartment) {
        messageText = 'Đã quay lại bước Trưởng phòng duyệt!'
      }

      message.info(messageText)
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    code,
    action_history,
    checkPermission,
    electronicDocumentStore,
    fileStore,
    loadingAnimationStore,
  ])

  // ==================== STEP 7: ĐÓNG DẤU ====================

  const handleDismissSign = useCallback(() => {
    if (!validateNextAssigneeOnApprove(ASSIGNEE_TYPE.DIGITAL_SIGNER)) {
      return
    }

    handleAction(
      ELECTRONIC_DOCUMENT_STATUS.READY,
      fileActive?.file_id,
      'Bạn đã bỏ qua bước ký số!',
      ASSIGNEE_TYPE.DIGITAL_SIGNER
    )
  }, [fileActive, handleAction, validateNextAssigneeOnApprove])

  const handleRevertSign = useCallback(async () => {
    try {
      loadingAnimationStore.showSpinner(true)

      const fileIdToUpdate = action_history?.find(
        el =>
          el.action === ELECTRONIC_DOCUMENT_STATUS.BOARD_OF_DIRECTOR_APPROVAL
      )?.file_id

      if (!fileIdToUpdate) {
        message.error('Không tìm thấy file để hủy ký số!')
        return
      }

      await electronicDocumentStore.updateElectronicDocumentStatus(
        {
          action: ELECTRONIC_DOCUMENT_STATUS.READY_REJECTION,
          file_id: fileIdToUpdate,
        },
        code
      )
      const { data } = await electronicDocumentStore.getElectronicDocumentById(
        code
      )
      await fileStore.downloadAttachment(
        data.action_history.find(el => el.action === data.status)?.file_id
      )
      message.info('Bạn đã quay trở về bước ký số')
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    action_history,
    code,
    electronicDocumentStore,
    fileStore,
    loadingAnimationStore,
  ])

  // ==================== STEP 8: PHÁT HÀNH ====================

  const handleCompleteDocument = useCallback(async () => {
    try {
      loadingAnimationStore.showSpinner(true)

      await electronicDocumentStore.updateElectronicDocumentStatus(
        {
          action: ELECTRONIC_DOCUMENT_STATUS.RELEASE,
          file_id: fileActive?.file_id,
        },
        code
      )

      await electronicDocumentStore.getElectronicDocumentById(code)

      if (fileActive?.file_id) {
        await fileStore.downloadAttachment(fileActive.file_id)
      }

      message.success('Tài liệu đã được hoàn thành!')
    } catch (err) {
      console.error('Lỗi khi hoàn thành tài liệu:', err)
      message.error(err.vi || 'Đã có lỗi xảy ra khi hoàn thành tài liệu!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    code,
    fileActive,
    electronicDocumentStore,
    fileStore,
    loadingAnimationStore,
  ])

  const handleRevertRelease = useCallback(async () => {
    try {
      loadingAnimationStore.showSpinner(true)

      const fileIdToUpdate = action_history?.find(
        el => el.action === ELECTRONIC_DOCUMENT_STATUS.RELEASE
      )?.file_id

      if (!fileIdToUpdate) {
        message.error('Không tìm thấy file để hủy phát hành!')
        return
      }

      await electronicDocumentStore.updateElectronicDocumentStatus(
        {
          action: ELECTRONIC_DOCUMENT_STATUS.RELEASE_REJECTION,
          file_id: fileIdToUpdate,
        },
        code
      )
      const { data } = await electronicDocumentStore.getElectronicDocumentById(
        code
      )
      await fileStore.downloadAttachment(
        data.action_history.find(el => el.action === data.status)?.file_id
      )
      message.info('Hãy phát hành lại tài liệu.')
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    action_history,
    code,
    electronicDocumentStore,
    fileStore,
    loadingAnimationStore,
  ])

  // ==================== VĂN THƯ & TRƯỞNG PHÒNG ====================

  const handleVTApproval = useCallback(() => {
    if (!validateNextAssigneeOnApprove(null)) {
      return
    }

    handleAction(
      ELECTRONIC_DOCUMENT_STATUS.VT_APPROVAL,
      fileActive?.file_id,
      'Văn thư đã duyệt thành công!',
      ASSIGNEE_TYPE.FORM_APPROVER
    )
  }, [fileActive, handleAction, validateNextAssigneeOnApprove])

  const handleOpenModalPopupVTSign = useCallback(() => {
    if (!fileActiveIsPDF) {
      message.info('Bạn chỉ được phép ký số file PDF')
      return
    }

    signedDocumentStore.setFileBlob(documentAttachment)
    signedDocumentStore.setOriginFileObj({ name: fileActive?.file_name })
    setPopupVisibility(prev => ({ ...prev, vtSign: true }))
  }, [fileActiveIsPDF, documentAttachment, fileActive, signedDocumentStore])

  const handleAfterVTSign = useCallback(
    async fileSignId => {
      setPopupVisibility(prev => ({ ...prev, vtSign: false }))
      await electronicDocumentStore.updateElectronicDocumentStatus(
        { action: ELECTRONIC_DOCUMENT_STATUS.VT_SIGNED, file_id: fileSignId },
        code
      )
      await electronicDocumentStore.getElectronicDocumentById(code)
      await fileStore.downloadAttachment(fileSignId)
    },
    [code, electronicDocumentStore, fileStore]
  )

  const handleRevertVTApproval = useCallback(async () => {
    if (!code) return
    try {
      loadingAnimationStore.showSpinner(true)

      const fileIdToUpdate = action_history?.find(
        el => el.action === ELECTRONIC_DOCUMENT_STATUS.CRE_APPROVAL
      )?.file_id

      if (!fileIdToUpdate) {
        message.error('Không tìm thấy file để hủy duyệt!')
        return
      }

      await electronicDocumentStore.updateElectronicDocumentStatus(
        {
          action: ELECTRONIC_DOCUMENT_STATUS.VT_REJECTION,
          file_id: fileIdToUpdate,
        },
        code
      )

      const { data } = await electronicDocumentStore.getElectronicDocumentById(
        code
      )
      await fileStore.downloadAttachment(
        data.action_history.find(el => el.action === data.status)?.file_id
      )
      message.info('Văn thư đã hủy duyệt')
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    action_history,
    code,
    assigneesMap,
    electronicDocumentStore,
    fileStore,
    loadingAnimationStore,
  ])

  const handleOpenTPPopupSign = useCallback(() => {
    if (!fileActiveIsPDF) {
      message.info('Bạn chỉ được phép ký số file PDF')
      return
    }

    signedDocumentStore.setFileBlob(documentAttachment)
    signedDocumentStore.setOriginFileObj({ name: fileActive?.file_name })
    setPopupVisibility(prev => ({ ...prev, tpSign: true }))
  }, [
    fileActiveIsPDF,
    documentAttachment,
    fileActive?.file_name,
    signedDocumentStore,
  ])

  const handleAfterTPSign = useCallback(
    async fileSignId => {
      setPopupVisibility(prev => ({ ...prev, tpSign: false }))
      await electronicDocumentStore.updateElectronicDocumentStatus(
        { action: ELECTRONIC_DOCUMENT_STATUS.TP_APPROVAL, file_id: fileSignId },
        code
      )
      await electronicDocumentStore.getElectronicDocumentById(code)
      await fileStore.downloadAttachment(fileSignId)
    },
    [code, electronicDocumentStore, fileStore]
  )

  const handleDismissTPSign = useCallback(() => {
    if (!validateNextAssigneeOnApprove(null)) {
      return
    }

    handleAction(
      ELECTRONIC_DOCUMENT_STATUS.TP_APPROVAL,
      fileActive?.file_id,
      'Đã bỏ qua bước ký số!',
      ASSIGNEE_TYPE.DEPARTMENT_APPROVER,
      () => {
        setSkipSignStates(prev => ({ ...prev, tp: true }))
        return true
      }
    )
  }, [fileActive, handleAction, validateNextAssigneeOnApprove])

  const handleRevertTPApproval = useCallback(async () => {
    try {
      loadingAnimationStore.showSpinner(true)
      const fileIdToUpdate =
        action_history?.find(
          el => el.action === ELECTRONIC_DOCUMENT_STATUS.CHANGE_DOC
        )?.file_id ||
        action_history?.find(
          el => el.action === ELECTRONIC_DOCUMENT_STATUS.PENDING
        )?.file_id

      if (!fileIdToUpdate) {
        throw new Error('Không tìm thấy tài liệu để quay lại!')
      }

      await electronicDocumentStore.updateElectronicDocumentStatus(
        {
          action: ELECTRONIC_DOCUMENT_STATUS.TP_REVERT,
          file_id: fileIdToUpdate,
        },
        code
      )

      const { data } = await electronicDocumentStore.getElectronicDocumentById(
        code
      )
      await fileStore.downloadAttachment(
        data.action_history.find(el => el.action === data.status)?.file_id
      )
      message.info('Đã quay lại bước Tạo dự thảo!')
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra khi quay lại bước Tạo dự thảo!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    code,
    action_history,
    assigneesMap,
    electronicDocumentStore,
    fileStore,
    loadingAnimationStore,
  ])

  const handleOpenTPCoPopupSign = useCallback(
    coSigner => {
      if (!fileActiveIsPDF) {
        message.info('Bạn chỉ được phép ký số file PDF')
        return
      }

      signedDocumentStore.setFileBlob(documentAttachment)
      signedDocumentStore.setOriginFileObj({ name: fileActive?.file_name })
      setPopupVisibility(prev => ({ ...prev, tpCoSign: true }))
    },
    [fileActiveIsPDF, documentAttachment, fileActive, signedDocumentStore]
  )

  const handleAfterTPCoSign = useCallback(
    async fileSignId => {
      if (!code) return
      try {
        loadingAnimationStore.showSpinner(true)

        await electronicDocumentStore.updateElectronicDocumentStatus(
          {
            action: ELECTRONIC_DOCUMENT_STATUS.TP_CO_SIGN,
            file_id: fileSignId,
          },
          code
        )
        await electronicDocumentStore.updateManagerSignedNote(
          currentUser.username,
          code
        )
        await electronicDocumentStore.getElectronicDocumentById(code)
        message.success(`Trưởng phòng đã ký thành công!`)

        if (fileSignId) {
          await fileStore.downloadAttachment(fileSignId)
        }

        setPopupVisibility(prev => ({ ...prev, tpCoSign: false }))
      } catch (err) {
        console.error('Error after signing:', err)
        message.error(err?.vi || 'Đã có lỗi xảy ra khi cập nhật tài liệu!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    },
    [
      code,
      currentUser,
      electronicDocumentStore,
      loadingAnimationStore,
      fileStore,
    ]
  )

  const handleRemoveCoSigner = useCallback(
    async coSigner => {
      if (
        !checkPermission(ASSIGNEE_TYPE.CREATE) &&
        !checkPermission(ASSIGNEE_TYPE.DEPARTMENT_APPROVER)
      ) {
        message.warning('Bạn không có quyền xóa trưởng phòng ký!')
        return
      }

      try {
        loadingAnimationStore.showSpinner(true)
        const dataSubmit = {
          new_items: [],
          remove_items: [
            {
              assignee_code: coSigner.assignee_code,
              assignee_type: coSigner.assignee_type,
              permission: ASSIGNEE_TYPE.DEPARTMENT_CO_SIGNER,
            },
          ],
        }
        await electronicDocumentStore.updateAssignPerson(code, dataSubmit)

        const {
          data,
        } = await electronicDocumentStore.getElectronicDocumentById(code)

        const updatedCoSigners = data.assignees
          .filter(el => el.permission === ASSIGNEE_TYPE.DEPARTMENT_CO_SIGNER)
          .map(coSigner => ({
            id: coSigner.assignee_code,
            full_name: coSigner.name_uppercase,
            image_id: coSigner.image,
            assignee_type: coSigner.assignee_type,
            permission: coSigner.permission,
          }))

        selectPeopleStore.setSelectUserData({
          ...selectPeopleStore.selectUserData,
          [ASSIGNEE_TYPE.DEPARTMENT_CO_SIGNER]: updatedCoSigners,
        })

        message.success('Xóa trưởng phòng ký thành công!')
      } catch (err) {
        console.error(err)
        message.error(err.vi || 'Đã có lỗi xảy ra!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    },
    [
      code,
      electronicDocumentStore,
      loadingAnimationStore,
      checkPermission,
      selectPeopleStore,
    ]
  )

  const handleSubmitCoSigners = useCallback(async () => {
    const { code } = selectedElectronicDocument
    const newCoSigners =
      selectPeopleStore.selectUserData[ASSIGNEE_TYPE.DEPARTMENT_CO_SIGNER] || []

    const new_items_assign = newCoSigners.map(user => ({
      assignee_code: user.id,
      assignee_type: user.assignee_type || ASSIGNEE_TYPE.USER,
      permission: ASSIGNEE_TYPE.DEPARTMENT_CO_SIGNER,
    }))

    if (newCoSigners.length === 0 && remove_items_assign.length === 0) {
      message.info('Vui lòng chọn ít nhất một trưởng phòng ký!')
      return
    }

    try {
      loadingAnimationStore.showSpinner(true)
      const dataSubmit = {
        new_items: new_items_assign,
        remove_items: [],
      }
      await electronicDocumentStore.updateAssignPerson(code, dataSubmit)
      await electronicDocumentStore.getElectronicDocumentById(code)
      selectPeopleStore.setIsVisibleSelectUserDepartmentPopup(false)
      message.success('Cập nhật trưởng phòng ký thành công!')
      setCurrentCoSignerIndex(0)
    } catch (err) {
      console.error('Error in handleSubmitCoSigners:', err)
      message.error(err?.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    selectedElectronicDocument,
    selectPeopleStore,
    electronicDocumentStore,
    loadingAnimationStore,
    assigneesMap,
  ])

  // ==================== HANDLERS CHO DUYỆT - CHUYỂN STEP ====================

  const handleLeaderApproval = useCallback(async () => {
    if (!validateAllFilesSignedBeforeApproval(1)) {
      return
    }

    if (!validateNextAssigneeOnApprove(ASSIGNEE_TYPE.LEADER_APPROVER)) {
      return
    }

    try {
      loadingAnimationStore.showSpinner(true)

      const fileId = main_files?.[0]?.file_id || fileActive?.file_id

      // ✅ GỬI KÈM MAIN_FILES để backend lưu trạng thái đầy đủ
      await electronicDocumentStore.updateElectronicDocumentStatus(
        {
          action: ELECTRONIC_DOCUMENT_STATUS.LEAD_APPROVAL,
          file_id: fileId,
          main_files: main_files, // ✅ Thêm dòng này
        },
        code
      )

      await electronicDocumentStore.getElectronicDocumentById(code)
      message.success('Trưởng đơn vị đã duyệt thành công!')
    } catch (err) {
      console.error('Error leader approval:', err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    main_files,
    fileActive,
    code,
    electronicDocumentStore,
    loadingAnimationStore,
    validateAllFilesSignedBeforeApproval,
    validateNextAssigneeOnApprove,
  ])

  const handleLeadKTATApproval = useCallback(async () => {
    if (!validateAllFilesSignedBeforeApproval(3)) {
      return
    }

    if (!validateNextAssigneeOnApprove(ASSIGNEE_TYPE.LEAD_KTAT_OFFICE)) {
      return
    }

    try {
      loadingAnimationStore.showSpinner(true)

      const fileId = main_files?.[0]?.file_id || fileActive?.file_id

      // ✅ GỬI KÈM MAIN_FILES
      await electronicDocumentStore.updateElectronicDocumentStatus(
        {
          action: ELECTRONIC_DOCUMENT_STATUS.LEAD_KTAT_OFFICE_APPROVAL,
          file_id: fileId,
          main_files: main_files, // ✅ Thêm dòng này
        },
        code
      )

      await electronicDocumentStore.getElectronicDocumentById(code)
      message.success('TP.KTAT đã duyệt thành công!')
    } catch (err) {
      console.error('Error lead KTAT approval:', err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    main_files,
    fileActive,
    code,
    electronicDocumentStore,
    loadingAnimationStore,
    validateAllFilesSignedBeforeApproval,
    validateNextAssigneeOnApprove,
  ])

  const handleBODApproval = useCallback(async () => {
    if (!validateAllFilesSignedBeforeApproval(4)) {
      return
    }

    if (!validateNextAssigneeOnApprove(ASSIGNEE_TYPE.BOARD_OF_DIRECTOR)) {
      return
    }

    try {
      loadingAnimationStore.showSpinner(true)

      const fileId = main_files?.[0]?.file_id || fileActive?.file_id

      // ✅ GỬI KÈM MAIN_FILES
      await electronicDocumentStore.updateElectronicDocumentStatus(
        {
          action: ELECTRONIC_DOCUMENT_STATUS.BOARD_OF_DIRECTOR_APPROVAL,
          file_id: fileId,
          main_files: main_files, // ✅ Thêm dòng này
        },
        code
      )

      await electronicDocumentStore.getElectronicDocumentById(code)
      message.success('Giám đốc đã duyệt thành công!')
    } catch (err) {
      console.error('Error BOD approval:', err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    main_files,
    fileActive,
    code,
    electronicDocumentStore,
    loadingAnimationStore,
    validateAllFilesSignedBeforeApproval,
    validateNextAssigneeOnApprove,
  ])

  const handleDigitalSignerApproval = useCallback(async () => {
    if (!validateAllFilesSignedBeforeApproval(5)) {
      return
    }

    if (!validateNextAssigneeOnApprove(ASSIGNEE_TYPE.DIGITAL_SIGNER)) {
      return
    }

    try {
      loadingAnimationStore.showSpinner(true)

      const fileId = main_files?.[0]?.file_id || fileActive?.file_id

      await electronicDocumentStore.updateElectronicDocumentStatus(
        {
          action: ELECTRONIC_DOCUMENT_STATUS.READY,
          file_id: fileId,
        },
        code
      )

      await electronicDocumentStore.getElectronicDocumentById(code)
      message.success('Đóng dấu hoàn tất!')
    } catch (err) {
      console.error('Error digital signer approval:', err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    main_files,
    fileActive,
    code,
    electronicDocumentStore,
    loadingAnimationStore,
    validateAllFilesSignedBeforeApproval,
    validateNextAssigneeOnApprove,
  ])

  const handleDownloadSignedFile = useCallback(async () => {
    if (!main_files || main_files.length === 0) {
      message.error('Không tìm thấy file để tải xuống')
      return
    }

    try {
      loadingAnimationStore.showSpinner(true)
      const zip = new JSZip()
      let hasFile = false

      for (const file of main_files) {
        const response = await fileStore.downloadFile(file.file_id)
        if (response && response.data) {
          zip.file(file.file_name || `tai_lieu_${file.file_id}.pdf`, response.data)
          hasFile = true
        }
      }

      if (!hasFile) {
        message.error('Không thể tải file, vui lòng thử lại sau')
        return
      }

      const content = await zip.generateAsync({ type: 'blob' })
      FileSaver.saveAs(content, 'tai_lieu_da_ky.zip')
    } catch (error) {
      console.error(error)
      message.error('Lỗi khi tải file zip')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [main_files, fileStore, loadingAnimationStore])

  // ==================== FILE UTILITIES ====================

  const submitRenameFile = useCallback(
    async value => {
      loadingAnimationStore.showSpinner(true)
      try {
        await fileStore.handleRenameFile(
          fileActive?.file_id,
          value.rename + renameFileState.ext
        )
        await electronicDocumentStore.getElectronicDocumentById(code)
        setRenameFileState(prev => ({ ...prev, visible: false }))
        message.success('Đổi tên tài liệu thành công!')
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Đã có lỗi xảy ra!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    },
    [
      code,
      electronicDocumentStore,
      fileActive,
      fileStore,
      loadingAnimationStore,
      renameFileState,
    ]
  )

  const handleOpenPopupRenameFile = useCallback(() => {
    const ext = '.' + utils.getExtensionFile(fileActive?.file_name)
    setRenameFileState({
      visible: true,
      ext,
      fields: [
        {
          name: ['rename'],
          value: utils.removeExtensionFile(fileActive?.file_name),
        },
      ],
    })
  }, [fileActive])

  // ==================== CONFIRM MODALS ====================

  const showRevertTPConfirm = useCallback(
    () =>
      confirm({
        title: 'Bạn có muốn quay lại bước Tạo dự thảo?',
        icon: <InfoCircleTwoTone />,
        okText: 'Đồng ý',
        okType: 'primary',
        cancelText: 'Hủy bỏ',
        onOk: handleRevertTPApproval,
      }),
    [handleRevertTPApproval]
  )

  const showRevertToLeaderConfirm = useCallback(() => {
    const hasLeader = assignees?.some(
      a => a.permission === ASSIGNEE_TYPE.LEADER_APPROVER
    )

    if (hasLeader) {
      confirm({
        title: 'Bạn có muốn quay lại bước Lãnh đạo duyệt?',
        icon: <InfoCircleTwoTone />,
        okText: 'Đồng ý',
        okType: 'primary',
        cancelText: 'Hủy bỏ',
        onOk: handleRevertToLeaderApproval,
      })
    } else {
      handleRevertToLeaderApproval()
    }
  }, [assignees, handleRevertToLeaderApproval])

  const showReleaseConfirm = useCallback(
    () =>
      confirm({
        title: 'Bạn có chắc chắn muốn phát hành tài liệu này?',
        icon: <InfoCircleTwoTone />,
        okText: 'Đồng ý',
        okType: 'primary',
        cancelText: 'Hủy bỏ',
        onOk: () => setPopupVisibility(prev => ({ ...prev, release: true })),
      }),
    []
  )

  const showRevertRelease = useCallback(
    () =>
      confirm({
        title: 'Bạn có chắc chắn muốn phát hành lại tài liệu này?',
        icon: <InfoCircleTwoTone />,
        okText: 'Đồng ý',
        okType: 'primary',
        cancelText: 'Hủy bỏ',
        onOk: handleRevertRelease,
      }),
    [handleRevertRelease]
  )

  const showSignRevertConfirm = useCallback(
    () =>
      confirm({
        title: 'Bạn có chắc chắn muốn đóng dấu lại tài liệu này?',
        icon: <InfoCircleTwoTone />,
        okText: 'Đồng ý',
        okType: 'primary',
        cancelText: 'Hủy bỏ',
        onOk: handleRevertSign,
      }),
    [handleRevertSign]
  )

  // ==================== STEP AND STATUS MANAGEMENT ====================

  useEffect(() => {
    if (!status) return

    const statusToStepMap = {
      [ELECTRONIC_DOCUMENT_STATUS.PENDING]: 0,
      [ELECTRONIC_DOCUMENT_STATUS.CHANGE_DOC]: 0,
      [ELECTRONIC_DOCUMENT_STATUS.TP_REVERT]: 0,
      [ELECTRONIC_DOCUMENT_STATUS.CANCEL]: 0,
      [ELECTRONIC_DOCUMENT_STATUS.LEAD_REJECTION]: 0,
      [ELECTRONIC_DOCUMENT_STATUS.ROLLBACK_TO_CREATOR]: 0,
      [ELECTRONIC_DOCUMENT_STATUS.FLOW_LOCKED]: 0,
      [ELECTRONIC_DOCUMENT_STATUS.FLOW_UNLOCK]: 0,
      [ELECTRONIC_DOCUMENT_STATUS.CRE_APPROVAL]: 1,
      [ELECTRONIC_DOCUMENT_STATUS.VT_REJECTION]: 1,
      [ELECTRONIC_DOCUMENT_STATUS.KTAT_OFFICE_REJECTION]: 1,
      [ELECTRONIC_DOCUMENT_STATUS.ROLLBACK_TO_LEADER]: 1,
      [ELECTRONIC_DOCUMENT_STATUS.LEAD_APPROVAL]: 2,
      [ELECTRONIC_DOCUMENT_STATUS.TP_APPROVAL]: 2,
      [ELECTRONIC_DOCUMENT_STATUS.LEAD_KTAT_OFFICE_REJECTION]: 2,
      [ELECTRONIC_DOCUMENT_STATUS.KTAT_OFFICE_APPROVAL]: 3,
      [ELECTRONIC_DOCUMENT_STATUS.FORM_REJECTION]: 3,
      [ELECTRONIC_DOCUMENT_STATUS.LEAD_KTAT_OFFICE_APPROVAL]: 4,
      [ELECTRONIC_DOCUMENT_STATUS.VT_APPROVAL]: 4,
      [ELECTRONIC_DOCUMENT_STATUS.VT_SIGNED]: 4,
      [ELECTRONIC_DOCUMENT_STATUS.BOARD_OF_DIRECTOR_APPROVAL]: 5,
      [ELECTRONIC_DOCUMENT_STATUS.BOARD_OF_DIRECTOR_REJECTION]: 4,
      [ELECTRONIC_DOCUMENT_STATUS.READY]: 6,
      [ELECTRONIC_DOCUMENT_STATUS.READY_REJECTION]: 5,
      [ELECTRONIC_DOCUMENT_STATUS.RELEASE]: 7,
      [ELECTRONIC_DOCUMENT_STATUS.RELEASE_REJECTION]: 6,
    }

    const newStep = statusToStepMap[status] ?? 0
    setCurrentStep(newStep)

    const stepOrder = [
      ASSIGNEE_TYPE.CREATE,
      ASSIGNEE_TYPE.LEADER_APPROVER,
      ASSIGNEE_TYPE.KTAT_OFFICE,
      ASSIGNEE_TYPE.LEAD_KTAT_OFFICE,
      ASSIGNEE_TYPE.BOARD_OF_DIRECTOR,
      ASSIGNEE_TYPE.DIGITAL_SIGNER,
      ASSIGNEE_TYPE.RELEASER,
    ]

    setStatusStep(
      stepOrder.map((_, i) =>
        i < newStep ? 'finish' : i === newStep ? 'process' : 'await'
      )
    )
  }, [status])

  useEffect(() => {
    if (!main_files || main_files.length === 0) {
      setFileActive(null)
      setFileActiveIsPDF(false)
      return
    }

    // ✅ Luôn lấy file đầu tiên từ main_files
    const currentFile = main_files[0]

    setFileActive({
      file_id: currentFile.file_id,
      file_name: currentFile.file_name,
    })

    const isPDFFile = utils.getExtensionFile(currentFile.file_name) === 'pdf'
    setFileActiveIsPDF(isPDFFile)
  }, [main_files])

  useEffect(() => {
    if (!action_history || action_history.length === 0) return

    const names = {
      creator_name: action_history.find(
        el => el.action === ELECTRONIC_DOCUMENT_STATUS.PENDING
      )?.user.name_lowercase,
      ktat_office_name: action_history.find(
        el => el.action === ELECTRONIC_DOCUMENT_STATUS.KTAT_OFFICE_APPROVAL
      )?.user.name_lowercase,
      leader_approval_name: action_history.find(
        el => el.action === ELECTRONIC_DOCUMENT_STATUS.LEAD_APPROVAL
      )?.user.name_lowercase,
      lead_ktat_office_name: action_history.find(
        el => el.action === ELECTRONIC_DOCUMENT_STATUS.LEAD_KTAT_OFFICE_APPROVAL
      )?.user.name_lowercase,
      form_approval_name: action_history.find(
        el => el.action === ELECTRONIC_DOCUMENT_STATUS.FORM_APPROVAL
      )?.user.name_lowercase,
      bod_approval_name: action_history.find(
        el =>
          el.action === ELECTRONIC_DOCUMENT_STATUS.BOARD_OF_DIRECTOR_APPROVAL
      )?.user.name_lowercase,
      regis_number_name: action_history.find(
        el => el.action === ELECTRONIC_DOCUMENT_STATUS.REGIS_NUMBER
      )?.user.name_lowercase,
      sign_document_name: action_history.find(
        el => el.action === ELECTRONIC_DOCUMENT_STATUS.READY
      )?.user.name_lowercase,
      release_name: action_history.find(
        el => el.action === ELECTRONIC_DOCUMENT_STATUS.RELEASE
      )?.user.name_lowercase,
    }
    setHandlerName(names)

    const isSkipSign =
      action_history.find(el => el.action === ELECTRONIC_DOCUMENT_STATUS.READY)
        ?.file_id ===
      action_history.find(
        el => el.action === ELECTRONIC_DOCUMENT_STATUS.REGIS_NUMBER
      )?.file_id
    setSkipSignStates(prev => ({ ...prev, sign: isSkipSign }))
  }, [action_history])

  useEffect(() => {
    setCurrentCoSignerIndex(0)
  }, [selectedElectronicDocument?.code])

  // ==================== RENDER UTILITIES ====================

  const renderField = useCallback(
    (label, content) => (
      <>
        <dt style={{ lineHeight: '28px' }}>{label}</dt>
        <dd
          style={{
            display: 'flex',
            alignItems: 'center',
          }}>
          {content}
        </dd>
      </>
    ),
    []
  )

  const renderApproverContent = useCallback(
    approver =>
      approver ? (
        <AvatarHasName imgId={approver.image} name={approver.name_uppercase} />
      ) : (
        <span style={{ fontSize: '12px' }}>–</span>
      ),
    []
  )

  const renderPdf = useMemo(
    () => (
      <ViewerWrapper>
        {viewingFileAttachment && selectedFileToView ? (
          <>
            <PDFViewer
              file={viewingFileAttachment}
              fileId={selectedFileToView.file_id}
              fileType={utils.getExtensionFile(selectedFileToView.file_name)}
            />
          </>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#999',
            }}>
            Chọn file để xem
          </div>
        )}
      </ViewerWrapper>
    ),
    [viewingFileAttachment, selectedFileToView]
  )

  const renderFiles = useCallback(
    (files, emptyText) => (
      <div>
        {files?.length > 0 ? (
          files.map(file => (
            <FileItem
              key={file.file_id}
              file_id={file.file_id}
              file_name={file.file_name}
              file_type={utils.getExtensionFile(file.file_name)}
            />
          ))
        ) : (
          <EmptyText>{emptyText}</EmptyText>
        )}
      </div>
    ),
    []
  )

  const menuDuThao = useMemo(
    () => (
      <Menu>
        <Menu.Item>
          <Upload
            accept={'.doc,.docx,application/pdf'}
            beforeUpload={() => false}
            showUploadList={false}
            onChange={onSelectDocument}>
            Chọn tài liệu khác
          </Upload>
        </Menu.Item>
      </Menu>
    ),
    [onSelectDocument]
  )

  const menuLeaderApproval = useMemo(
    () => (
      <Menu>
        <Menu.Item onClick={handleSkipSignLeaderApproval}>
          Bỏ qua ký số
        </Menu.Item>
      </Menu>
    ),
    [handleSkipSignLeaderApproval]
  )

  const menuLeadKTATApproval = useMemo(
    () => (
      <Menu>
        <Menu.Item onClick={handleSkipSignLeadKTATApproval}>
          Bỏ qua ký số
        </Menu.Item>
      </Menu>
    ),
    [handleSkipSignLeadKTATApproval]
  )

  const menuBGDDocument = useMemo(
    () => (
      <Menu>
        <Menu.Item onClick={handleSkipBGDSign}>Bỏ qua ký số</Menu.Item>
      </Menu>
    ),
    [handleSkipBGDSign]
  )

  const menuSignDocument = useMemo(
    () => (
      <Menu>
        <Menu.Item onClick={handleDismissSign}>Bỏ qua ký số</Menu.Item>
        <Menu.Item onClick={showRevertToLeaderConfirm}>Duyệt lại</Menu.Item>
      </Menu>
    ),
    [handleDismissSign, showRevertToLeaderConfirm]
  )

  const menuReleaseDocument = useMemo(
    () => (
      <Menu>
        {checkPermission(ASSIGNEE_TYPE.DIGITAL_SIGNER) && (
          <Menu.Item onClick={showSignRevertConfirm}>Đóng dấu lại</Menu.Item>
        )}
        {/* <Menu.Item onClick={handleOpenPopupRenameFile}>
          Đổi tên tài liệu
        </Menu.Item> */}
      </Menu>
    ),
    [checkPermission, showSignRevertConfirm, handleOpenPopupRenameFile]
  )

  const stepsData = useMemo(() => {
    const stepConfigs = [
      {
        title: 'Tạo dự thảo',
        assignee: null,
        nameKey: 'creator_name',
        minStep: 0,
      },
      {
        title: 'Trưởng đơn vị ký',
        assignee: ASSIGNEE_TYPE.LEADER_APPROVER,
        nameKey: 'leader_approval_name',
        minStep: 0,
      },
      {
        title: 'P.KTAT duyệt thể thức',
        assignee: ASSIGNEE_TYPE.KTAT_OFFICE,
        nameKey: 'ktat_office_name',
        minStep: 0,
      },
      {
        title: 'TP.KTAT ký',
        assignee: ASSIGNEE_TYPE.LEAD_KTAT_OFFICE,
        nameKey: 'lead_ktat_office_name',
        minStep: 0,
      },
      {
        title: 'Giám đốc ký',
        assignee: ASSIGNEE_TYPE.BOARD_OF_DIRECTOR,
        nameKey: 'bod_approval_name',
        minStep: 0,
      },
      {
        title: 'Đóng dấu',
        assignee: ASSIGNEE_TYPE.DIGITAL_SIGNER,
        nameKey: 'sign_document_name',
        minStep: 0,
      },
      {
        title: 'Phát hành',
        assignee: ASSIGNEE_TYPE.RELEASER,
        nameKey: 'release_name',
        minStep: 0,
      },
    ]

    return stepConfigs
      .map((step, index) => {
        const showStep = !step.assignee || checkPermission(step.assignee)
        if (!showStep) return null

        const name = handlerName[step.nameKey]

        const showDescription = currentStep >= index + 1

        return (
          <Step
            key={index}
            title={step.title}
            status={statusStep[index]}
            icon={
              <CustomStepIcon stepNumber={index + 1} currentStep={currentStep}>
                {currentStep >= index + 1 ? <CheckOutlined /> : index + 1}
              </CustomStepIcon>
            }
            description={
              name &&
              showDescription && (
                <div>
                  <span style={{ color: '#000', fontWeight: 600 }}>
                    {utils.getNameInCapitalize(name)}
                  </span>
                </div>
              )
            }
          />
        )
      })
      .filter(Boolean)
  }, [handlerName, currentStep, statusStep, checkPermission])

  const renderSignGatedApprovalButton = useCallback(
    (label, onClick) => {
      const { canApprove, unsignedCount } = currentApprovalBlocker
      const button = (
        <Button
          size="large"
          onClick={onClick}
          type="primary"
          disabled={!canApprove}
          style={{ width: 380 }}>
          <CheckOutlined style={{ marginRight: 4 }} /> {label}
        </Button>
      )

      if (canApprove) {
        return button
      }

      return (
        <Tooltip
          title={`Còn ${unsignedCount} file PDF chưa ký số. Vui lòng ký hết trước khi duyệt.`}>
          <span style={{ display: 'inline-block', width: 380 }}>{button}</span>
        </Tooltip>
      )
    },
    [currentApprovalBlocker]
  )

  const stepActions = useMemo(
    () => [
      {
        step: 0,
        acl: ACL_ACTION_TYPE.du_thao__DIGITAL_DOCUMENT,
        label: 'Trình dự thảo',
        onClick: handlePresentDuThao,
        condition: () => isAssigneeOfStep(ASSIGNEE_TYPE.CREATE),
        overlay: null,
      },
      {
        step: 1,
        acl: ACL_ACTION_TYPE.leader_approval__DIGITAL_DOCUMENT,
        render: () => {
          if (!isAssigneeOfStep(ASSIGNEE_TYPE.LEADER_APPROVER)) return null
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
              {renderSignGatedApprovalButton(
                'Trưởng đơn vị duyệt',
                handleLeaderApproval
              )}
              {renderRejectButton()}
            </div>
          )
        },
      },
      {
        step: 2,
        acl: ACL_ACTION_TYPE.ktat_office__DIGITAL_DOCUMENT,
        render: () => {
          if (!isAssigneeOfStep(ASSIGNEE_TYPE.KTAT_OFFICE)) return null
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
              <Button
                size="large"
                onClick={handleKTATApproval}
                type="primary"
                style={{ width: 380 }}
                disabled={isFlowLocked}>
                <CheckOutlined style={{ marginRight: 4 }} /> P.KTAT duyệt thể
                thức
              </Button>
              {renderRejectButton()}
            </div>
          )
        },
      },
      {
        step: 3,
        acl: ACL_ACTION_TYPE.lead_ktat_approval__DIGITAL_DOCUMENT,
        render: () => {
          if (!isAssigneeOfStep(ASSIGNEE_TYPE.LEAD_KTAT_OFFICE)) return null
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
              {renderSignGatedApprovalButton(
                'TP.KTAT duyệt',
                handleLeadKTATApproval
              )}
              {renderRejectButton()}
            </div>
          )
        },
      },
      {
        step: 4,
        acl: ACL_ACTION_TYPE.bod_approval__DIGITAL_DOCUMENT,
        render: () => {
          if (!isAssigneeOfStep(ASSIGNEE_TYPE.BOARD_OF_DIRECTOR)) return null
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
              {renderSignGatedApprovalButton(
                'Giám đốc duyệt',
                handleBODApproval
              )}
              {renderRejectButton()}
            </div>
          )
        },
      },
      {
        step: 5,
        acl: ACL_ACTION_TYPE.dong_dau__DIGITAL_DOCUMENT,
        render: () => {
          if (!isAssigneeOfStep(ASSIGNEE_TYPE.DIGITAL_SIGNER)) return null
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
              {currentApprovalBlocker.canApprove ? (
                <Dropdown.Button
                  size="large"
                  onClick={handleDigitalSignerApproval}
                  type="primary"
                  overlay={menuSignDocument}
                  style={{ width: 380 }}>
                  <CheckOutlined style={{ marginRight: 4 }} /> Duyệt đóng dấu
                </Dropdown.Button>
              ) : (
                <Tooltip
                  title={`Còn ${currentApprovalBlocker.unsignedCount} file PDF chưa ký số. Vui lòng ký hết trước khi duyệt.`}>
                  <span style={{ display: 'inline-block', width: 380 }}>
                    <Dropdown.Button
                      size="large"
                      onClick={handleDigitalSignerApproval}
                      type="primary"
                      overlay={menuSignDocument}
                      disabled
                      style={{ width: '100%' }}>
                      <CheckOutlined style={{ marginRight: 4 }} /> Duyệt đóng
                      dấu
                    </Dropdown.Button>
                  </span>
                </Tooltip>
              )}
            </div>
          )
        },
      },
      {
        step: 6,
        acl: ACL_ACTION_TYPE.hoan_thanh__DIGITAL_DOCUMENT,
        label: 'Phát hành tài liệu',
        onClick: showReleaseConfirm,
        condition: () => isAssigneeOfStep(ASSIGNEE_TYPE.RELEASER),
        overlay: menuReleaseDocument,
      },
    ],
    [
      currentApprovalBlocker,
      handlePresentDuThao,
      handleKTATApproval,
      handleLeaderApproval,
      handleLeadKTATApproval,
      handleFormApproval,
      handleBODApproval,
      handleDigitalSignerApproval,
      handleCompleteDocument,
      isAssigneeOfStep,
      isFlowLocked,
      menuDuThao,
      menuLeaderApproval,
      menuLeadKTATApproval,
      menuBGDDocument,
      menuSignDocument,
      menuReleaseDocument,
      renderSignGatedApprovalButton,
      renderRejectButton,
    ]
  )

  return (
    <>
      {isFlowLocked && (
        <FlowLockedBanner>
          <FlowLockedContent>
            <FlowLockedIcon>
              <LockOutlined />
            </FlowLockedIcon>
            <FlowLockedText>
              <FlowLockedTitle>Luồng trình ký đã bị khóa</FlowLockedTitle>
              <FlowLockedDesc>
                Vượt quá 02 lần trả về chỉnh sửa. TP.KTAT chủ trì họp trước khi
                Giám đốc mở lại luồng.
              </FlowLockedDesc>
            </FlowLockedText>
          </FlowLockedContent>
          {isAssigneeOfStep(ASSIGNEE_TYPE.BOARD_OF_DIRECTOR) && (
            <FlowLockedAction>
              <Button type="primary" onClick={handleUnlockFlow}>
                Mở lại luồng
              </Button>
            </FlowLockedAction>
          )}
        </FlowLockedBanner>
      )}
      <CanvasDocHeader>
        <Steps
          labelPlacement={'vertical'}
          size={'small'}
          current={currentStep}
          direction="horizontal"
          style={{ marginBottom: 12 }}>
          {stepsData}
        </Steps>
        <ButtonItem>
          {!isFlowLocked &&
            stepActions.map(item => {
              if (item.step !== currentStep) return null

              if (
                item.step === 1 &&
                checkPermission(ASSIGNEE_TYPE.DEPARTMENT_CO_SIGNER) &&
                !checkPermission(ASSIGNEE_TYPE.DEPARTMENT_APPROVER)
              ) {
                return null
              }

              if (item.condition && !item.condition()) {
                return null
              }

              return (
                <>
                  {item.render ? (
                    item.render()
                  ) : item.overlay ? (
                    <Dropdown.Button
                      size="large"
                      trigger={['click']}
                      onClick={item.onClick}
                      overlay={item.overlay}
                      type="primary">
                      <CheckOutlined style={{ marginRight: 4 }} /> {item.label}
                    </Dropdown.Button>
                  ) : (
                    <Button
                      size="large"
                      type="primary"
                      style={{ width: '380px' }}
                      onClick={item.onClick}>
                      <CheckOutlined style={{ marginRight: 4 }} /> {item.label}
                    </Button>
                  )}
                </>
              )
            })}
        </ButtonItem>
      </CanvasDocHeader>
      <CanvasDocWrapper>
        {renderPdf}
        <BlockRight>
          <ContentBlockWrapper style={{ marginBottom: 20 }}>
            <TitleContentBox>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>Tài liệu trình ký</span>
                {currentStep >= 2 && (
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleDownloadSignedFile}
                    icon={<DownloadOutlined />}
                  >
                    Tải tất cả tài liệu đã ký
                  </Button>
                )}
              </div>
            </TitleContentBox>
            <ElectronicDocumentInfo>
              <MainFilesList
                mainFiles={main_files}
                activeSignConfig={activeSignConfig}
                selectedFileToView={selectedFileToView}
                onSelectFile={handleSelectFileToView}
                onDirectSign={handleDirectSignForFile}
                onBatchSignAll={handleBatchSignAllForStep}
              />
            </ElectronicDocumentInfo>
          </ContentBlockWrapper>
          <ContentBlockWrapper style={{ marginBottom: 20 }}>
            <TitleContentBox>Thông tin chung</TitleContentBox>
            <ElectronicDocumentInfo>
              {(rollbackCount > 0 || isFlowLocked) &&
                renderField(
                  'Trả về chỉnh sửa',
                  <RollbackCountBadge
                    $danger={isFlowLocked || rollbackCount >= MAX_ROLLBACK}>
                    <RollbackCountValue>
                      {Math.min(rollbackCount, MAX_ROLLBACK)}/{MAX_ROLLBACK}
                    </RollbackCountValue>
                    {isFlowLocked && <span>Đã khóa</span>}
                  </RollbackCountBadge>
                )}
              {checkPermission(ASSIGNEE_TYPE.LEADER_APPROVER) &&
                renderField(
                  'Trưởng đơn vị ký',
                  renderApproverContent(assigneesMap.leaderApprover)
                )}
              {checkPermission(ASSIGNEE_TYPE.KTAT_OFFICE) &&
                renderField(
                  'P.KTAT duyệt thể thức',
                  renderApproverContent(assigneesMap.ktatOffice)
                )}
              {checkPermission(ASSIGNEE_TYPE.LEAD_KTAT_OFFICE) &&
                renderField(
                  'TP.KTAT ký',
                  renderApproverContent(assigneesMap.leadKtatOffice)
                )}
              {checkPermission(ASSIGNEE_TYPE.BOARD_OF_DIRECTOR) &&
                renderField(
                  'Giám đốc ký',
                  renderApproverContent(assigneesMap.boardOfDirector)
                )}

              {checkPermission(ASSIGNEE_TYPE.DIGITAL_SIGNER) &&
                renderField(
                  'Đóng dấu',
                  renderApproverContent(assigneesMap.digitalSigner)
                )}
              {checkPermission(ASSIGNEE_TYPE.RELEASER) &&
                renderField(
                  'Phát hành',
                  renderApproverContent(assigneesMap.releaser)
                )}
              {renderField(
                'Tài liệu đính kèm',
                renderFiles(attachments, 'Không có tài liệu đính kèm')
              )}
            </ElectronicDocumentInfo>
          </ContentBlockWrapper>
          <ContentBlockWrapper style={{ position: 'relative' }}>
            <TitleContentBox>Ý kiến</TitleContentBox>
            <CommentBlock signDocumentId={code} />
            <SpinnerInlineComponent sizeSpin={'small'} />
          </ContentBlockWrapper>
          <ContentBlockWrapper style={{ position: 'relative', marginTop: 20 }}>
            <TitleContentBox>Lịch sử hoạt động</TitleContentBox>
            <InternalDocumentSignDocHistory
              historyAction={electronicDocumentStore.electronicHistory}
              status={electronicDocumentStore.electronicCurentAction}
              historyId={code}
              historyLists={historyLists}
            />
          </ContentBlockWrapper>
        </BlockRight>
      </CanvasDocWrapper>
      {/* ==================== POPUPS ==================== */}
      <PopupRejectDocument
        visible={popupVisibility.reject}
        onCancel={() =>
          setPopupVisibility(prev => ({ ...prev, reject: false }))
        }
        onOk={handleRejectSubmit}
        loading={rejectLoading}
        currentStep={currentStep}
        rollbackCount={rollbackCount}
      />
      <PopupRenameFile
        isModalVisibleRenameFile={renameFileState.visible}
        handleCloseModalRenameFile={() =>
          setRenameFileState(prev => ({ ...prev, visible: false }))
        }
        submitRenameFile={submitRenameFile}
        fieldsRenameFile={renameFileState.fields}
        fileExt={renameFileState.ext}
      />
      {popupVisibility.release && (
        <PopupReleaseElectronicDocument
          fileActive={main_files}
          selectedElectronicDocument={selectedElectronicDocument}
          handleClosePopupElectronicDocument={() =>
            setPopupVisibility(prev => ({ ...prev, release: false }))
          }
          isVisiblePopupRelease={popupVisibility.release}
        />
      )}

      {popupVisibility.tpCoSign && (
        <PopupTPApproverSign
          isVisiblePopupSign={popupVisibility.tpCoSign}
          handleClosePopupSign={() =>
            setPopupVisibility(prev => ({ ...prev, tpCoSign: false }))
          }
          hiddenSelectDocument
          fileId={fileActive?.file_id}
          handleAfterSign={handleAfterTPCoSign}
          currentCoSigner={coSigners[currentCoSignerIndex]}
          coSigners={coSigners}
        />
      )}
      <SelectPeoplePopup
        onlyOneUser={false}
        disableSelectDepartment
        handleCancelSelectUser={handleCancelSelectUserDepartment}
        handleSubmitSelectUser={handleSubmitCoSigners}
        isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
      />
    </>
  )
}

export default inject(
  'signedDocumentStore',
  'loadingAnimationStore',
  'authenticationStore',
  'fileStore',
  'electronicDocumentStore',
  'listHistoryLogStore',
  'selectPeopleStore',
  'aclStore'
)(observer(CanvasDoc))
